import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Tabs,
  Tab,
  Avatar,
  Chip,
  Button,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Rating,
  Badge,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  ArrowLeft,
  Mail,
  Calendar,
  Briefcase,
  Target,
  CheckCircle,
  Clock,
  TrendingUp,
  Star,
  Award,
  Users,
  BarChart3,
  Edit2,
  Plus,
  AlertCircle,
  ChevronRight,
  Timer,
  FileText,
  MessageSquare,
  Check,
  X,
} from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { useAppSelector } from '@/store';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { 
  userService, 
  taskService, 
  projectService, 
  reviewService, 
  bandwidthService, 
  leaveService, 
  kraKpiService,
  timeService
} from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { User, UserRole } from '@/types';

dayjs.extend(relativeTime);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`member-tabpanel-${index}`} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const getRoleColor = (role: UserRole): 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' => {
  switch (role) {
    case UserRole.ADMIN: return 'error';
    case UserRole.DEPARTMENT_HEAD: return 'primary';
    case UserRole.PROJECT_MANAGER: return 'info';
    case UserRole.TEAM_LEAD: return 'warning';
    case UserRole.MANAGER: return 'primary';
    default: return 'secondary';
  }
};

const getRoleLabel = (role: UserRole): string => {
  const labels: Record<UserRole, string> = {
    [UserRole.ADMIN]: 'Admin',
    [UserRole.DEPARTMENT_HEAD]: 'Department Head',
    [UserRole.PROJECT_MANAGER]: 'Project Manager',
    [UserRole.TEAM_LEAD]: 'Team Lead',
    [UserRole.MANAGER]: 'Manager',
    [UserRole.INDIVIDUAL_CONTRIBUTOR]: 'Contributor',
  };
  return labels[role] || 'Team Member';
};

const TeamMemberDetailPage: React.FC = () => {
  const { memberId, userId } = useParams<{ memberId?: string; userId?: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [addReviewOpen, setAddReviewOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState<number | null>(4);
  const [reviewFeedback, setReviewFeedback] = useState('');
  const [assignTaskOpen, setAssignTaskOpen] = useState(false);

  // Get member data from backend API
  const id = memberId || userId;
  
  const { data: member, isLoading: memberLoading } = useQuery({
    queryKey: QUERY_KEYS.user(id || ''),
    queryFn: () => userService.getUser(id || ''),
    enabled: !!id,
  });

  // Get member's tasks from backend
  const { data: tasksResponse } = useQuery({
    queryKey: ['tasks', 'user', id],
    queryFn: () => taskService.getTasks({ assignedTo: id }),
    enabled: !!id,
  });

  // Get member's projects
  const { data: projectsResponse } = useQuery({
    queryKey: ['projects', 'user', id],
    queryFn: () => projectService.getProjects({ ownerId: id }),
    enabled: !!id,
  });

  // Get member's KRAs
  const { data: krasData = [] } = useQuery({
    queryKey: ['kras', 'user', id],
    queryFn: () => kraKpiService.getKRAs(id),
    enabled: !!id,
  });

  // Get member's KPIs
  const { data: kpisData = [] } = useQuery({
    queryKey: ['kpis', 'user', id],
    queryFn: () => kraKpiService.getKPIsForUser(id || ''),
    enabled: !!id,
  });

  // Get member's reviews
  const { data: reviewsResponse } = useQuery({
    queryKey: ['reviews', 'user', id],
    queryFn: () => reviewService.getReviews({ reviewerId: id }),
    enabled: !!id,
  });

  // Get member's bandwidth
  const { data: bandwidthResponse } = useQuery({
    queryKey: ['bandwidth', 'user', id],
    queryFn: () => bandwidthService.getUserBandwidth(id || ''),
    enabled: !!id,
  });

  // Get member's leave calendar
  const { data: calendarResponse } = useQuery({
    queryKey: ['calendar', 'user', id],
    queryFn: () => {
      const start = dayjs().format('YYYY-MM-DD');
      const end = dayjs().add(30, 'day').format('YYYY-MM-DD');
      return leaveService.getLeaveCalendar(start, end);
    },
    enabled: !!id,
  });

  // Get member's time tracking summary
  const { data: timeTrackingData } = useQuery({
    queryKey: ['timeTracking', 'user', id],
    queryFn: () => timeService.getUserTimeSummary(id || ''),
    enabled: !!id,
  });

  // Get member's leave balance
  const { data: leaveBalanceResponse } = useQuery({
    queryKey: ['leaveBalance', 'user', id],
    queryFn: () => leaveService.getUserLeaveBalance(id || ''),
    enabled: !!id,
  });

  // Get member's leave requests (for pending approvals)
  const { data: leaveRequestsResponse } = useQuery({
    queryKey: ['leaveRequests', 'user', id],
    queryFn: () => leaveService.getUserLeaveRequests(id || ''),
    enabled: !!id,
  });

  const tasksData = tasksResponse?.items || [];
  const projects = projectsResponse?.items || [];
  const reviews = Array.isArray(reviewsResponse?.data) 
    ? reviewsResponse.data 
    : (Array.isArray(reviewsResponse?.data?.data) ? reviewsResponse.data.data : []);
  const bandwidth = bandwidthResponse?.data;
  const calendar = Array.isArray(calendarResponse?.data)
    ? calendarResponse.data
    : (Array.isArray(calendarResponse?.data?.data) ? calendarResponse.data.data : []);

  // Compute real data from API responses
  const realData = useMemo(() => {
    if (!member) return null;

    // Map tasks
    const tasks = (tasksData || []).map((task) => ({
      id: task.id,
      title: task.title,
      project: task.projectName || '',
      status: task.status,
      priority: task.priority,
      dueDate: task.dueDate,
    }));

    // Map projects
    const mappedProjects = (projects || []).map((project) => ({
      id: project.id,
      name: project.name,
      role: 'Contributor', // Can be enhanced based on actual role data
      progress: project.completionPercentage || 0,
      status: project.status === 'active' ? 'On Track' : 
              project.status === 'completed' ? 'Completed' : 'At Risk',
      tasks: project.taskCount || 0,
    }));

    // Map KRAs
    const mappedKras = (krasData || []).map((kra) => ({
      id: kra.id,
      title: kra.title,
      description: kra.description || '',
      progress: kra.progress,
      status: kra.status,
      startDate: kra.startDate,
      endDate: kra.endDate,
      weight: kra.weight,
    }));

    // Map KPIs
    const mappedKpis = (kpisData || []).map((kpi) => ({
      id: kpi.id,
      kraId: kpi.kraId,
      title: kpi.title,
      current: kpi.current,
      target: kpi.target,
      unit: kpi.unit,
      frequency: kpi.frequency,
    }));

    // Map reviews
    const mappedReviews = (reviews || []).slice(0, 3).map((review: any) => ({
      id: review.id,
      reviewer: review.reviewerName || 'Unknown',
      rating: review.rating || 4.0,
      date: review.createdAt || dayjs().toISOString(),
      period: review.period || 'Recent',
      feedback: review.feedback || review.comment || 'No feedback provided',
      strengths: review.strengths || [],
      improvements: review.improvements || [],
    }));

    // Map bandwidth
    const bandwidthData = bandwidth ? {
      allocated: bandwidth.utilizationPercent || member.utilizationPercent,
      available: 100 - (bandwidth.utilizationPercent || member.utilizationPercent),
      hoursPerWeek: 40,
      allocatedHours: bandwidth.allocatedHours || Math.round(40 * member.utilizationPercent / 100),
      availableHours: bandwidth.availableHours || Math.round(40 * (100 - member.utilizationPercent) / 100),
      breakdown: bandwidth.projectBreakdown || [],
    } : {
      allocated: member.utilizationPercent,
      available: 100 - member.utilizationPercent,
      hoursPerWeek: 40,
      allocatedHours: Math.round(40 * member.utilizationPercent / 100),
      availableHours: Math.round(40 * (100 - member.utilizationPercent) / 100),
      breakdown: [],
    };

    // Map calendar events from leave requests
    const mappedCalendar = (calendar || []).map((leave: any) => ({
      id: leave.id,
      title: leave.type ? `${leave.type} Leave` : 'Event',
      date: leave.startDate,
      time: 'All Day',
      type: 'leave',
    }));

    // Time tracking from real API data
    const timeTracking = timeTrackingData || {
      thisWeek: { logged: 0, expected: 40 },
      lastWeek: { logged: 0, expected: 40 },
      thisMonth: { logged: 0, expected: 160 },
      breakdown: [],
    };

    // Leave balance from real API data
    const leaveBalance = leaveBalanceResponse?.data || {
      available: 0,
      used: 0,
      pending: 0,
      total: 0,
    };

    // Pending leave requests
    const pendingLeaveRequests = Array.isArray(leaveRequestsResponse?.data) 
      ? leaveRequestsResponse.data.filter((req: any) => req.status === 'pending')
      : [];

    return {
      tasks,
      projects: mappedProjects,
      kras: mappedKras,
      kpis: mappedKpis,
      reviews: mappedReviews,
      bandwidth: bandwidthData,
      calendar: mappedCalendar,
      timeTracking,
      leaveBalance,
      pendingLeaveRequests,
    };
  }, [member, tasksData, projects, krasData, kpisData, reviews, bandwidth, calendar, timeTrackingData, leaveBalanceResponse, leaveRequestsResponse]);

  // Use realData instead of mockData
  const mockData = realData;

  if (!member) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowLeft size={16} />} onClick={() => navigate('/team')} sx={{ mb: 2 }}>
          Back to Team
        </Button>
        <Alert severity="error">Team member not found. Please select a valid team member.</Alert>
      </Box>
    );
  }

  const handleSaveReview = () => {
    console.log('Saving review:', { rating: reviewRating, feedback: reviewFeedback });
    setAddReviewOpen(false);
    setReviewRating(4);
    setReviewFeedback('');
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'success';
      case 'in progress': return 'primary';
      case 'not started': return 'default';
      case 'on track': return 'success';
      case 'at risk': return 'warning';
      case 'overdue': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header with Back Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/team')} size="small" sx={{ border: '1px solid #ddd' }}>
          <ArrowLeft size={20} />
        </IconButton>
        <Box>
          <Typography variant="body2" color="text.secondary">Team / Member Profile</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>{member.name}</Typography>
        </Box>
      </Box>

      {/* Profile Header Card */}
      <Card sx={{ mb: 3, border: `4px solid`, borderColor: `${getRoleColor(member.role)}.main`, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            {/* Profile Info */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  variant="dot"
                  sx={{ '& .MuiBadge-badge': { backgroundColor: member.isActive ? '#44b700' : '#f50057', width: 14, height: 14, border: '2px solid white' } }}
                >
                  <Avatar sx={{ width: 100, height: 100, bgcolor: `${getRoleColor(member.role)}.main`, fontSize: '2.5rem' }}>
                    {member.avatar}
                  </Avatar>
                </Badge>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>{member.name}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
                    <Chip label={getRoleLabel(member.role)} size="small" color={getRoleColor(member.role)} />
                    <Chip label={member.department} size="small" variant="outlined" />
                  </Stack>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Mail size={14} /> {member.email}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <Calendar size={12} /> Joined {dayjs(member.joinDate).fromNow()} • {member.timezone}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Quick Stats */}
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2' }}>
                      {mockData?.tasks.filter(t => t.status !== 'Completed').length}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 600 }}>Active Tasks</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#e8f5e9', borderRadius: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#388e3c' }}>
                      {mockData?.projects.length}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#388e3c', fontWeight: 600 }}>Projects</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#fff3e0', borderRadius: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#f57c00' }}>
                      {member.utilizationPercent}%
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#f57c00', fontWeight: 600 }}>Utilization</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#fce4ec', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <Star size={18} style={{ color: '#c2185b' }} />
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#c2185b' }}>
                        {member.performanceRating}
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: '#c2185b', fontWeight: 600 }}>Rating</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="contained" size="small" startIcon={<Plus size={16} />} onClick={() => setAssignTaskOpen(true)}>
              Assign Task
            </Button>
            <Button variant="outlined" size="small" startIcon={<Calendar size={16} />} onClick={() => setActiveTab(5)}>
              View Calendar
            </Button>
            <Button variant="outlined" size="small" startIcon={<MessageSquare size={16} />} onClick={() => setActiveTab(4)}>
              Add Review
            </Button>
            <Button variant="outlined" size="small" startIcon={<Target size={16} />} onClick={() => setActiveTab(2)}>
              View KRA/KPIs
            </Button>
            <Button variant="outlined" size="small" startIcon={<BarChart3 size={16} />} onClick={() => setActiveTab(3)}>
              Bandwidth
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Skills */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Skills & Expertise</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {(member.skills || []).map((skill) => (
              <Chip key={skill} label={skill} size="small" variant="outlined" color="primary" />
            ))}
          </Stack>
        </CardContent>
      </Card>

      {/* Main Content Tabs */}
      <Card>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 2 }}
        >
          <Tab label="Overview" icon={<Users size={16} />} iconPosition="start" />
          <Tab label="Tasks & Projects" icon={<CheckCircle size={16} />} iconPosition="start" />
          <Tab label="KRA / KPI" icon={<Target size={16} />} iconPosition="start" />
          <Tab label="Bandwidth" icon={<BarChart3 size={16} />} iconPosition="start" />
          <Tab label="Reviews" icon={<Star size={16} />} iconPosition="start" />
          <Tab label="Calendar" icon={<Calendar size={16} />} iconPosition="start" />
          <Tab label="Time Tracking" icon={<Timer size={16} />} iconPosition="start" />
          <Tab label="Leave Approval" icon={<Calendar size={16} />} iconPosition="start" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Tab 0: Overview */}
          <TabPanel value={activeTab} index={0}>
            <Grid container spacing={3}>
              {/* Performance Summary */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardHeader title="Performance Summary" titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }} />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Productivity</Typography>
                        <Typography variant="h6" fontWeight={600}>85%</Typography>
                        <LinearProgress variant="determinate" value={85} sx={{ height: 6, borderRadius: 3, mt: 0.5 }} />
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Quality</Typography>
                        <Typography variant="h6" fontWeight={600}>92%</Typography>
                        <LinearProgress variant="determinate" value={92} color="success" sx={{ height: 6, borderRadius: 3, mt: 0.5 }} />
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Time Management</Typography>
                        <Typography variant="h6" fontWeight={600}>78%</Typography>
                        <LinearProgress variant="determinate" value={78} color="warning" sx={{ height: 6, borderRadius: 3, mt: 0.5 }} />
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Collaboration</Typography>
                        <Typography variant="h6" fontWeight={600}>88%</Typography>
                        <LinearProgress variant="determinate" value={88} color="info" sx={{ height: 6, borderRadius: 3, mt: 0.5 }} />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Current Workload */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardHeader title="Current Workload" titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }} />
                  <CardContent>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">Capacity Utilization</Typography>
                        <Typography variant="body2" fontWeight={600}>{member.utilizationPercent}%</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={member.utilizationPercent}
                        sx={{ height: 10, borderRadius: 5 }}
                        color={member.utilizationPercent > 100 ? 'error' : member.utilizationPercent > 85 ? 'warning' : 'success'}
                      />
                    </Box>
                    <Stack spacing={1}>
                      {(mockData?.bandwidth.breakdown || []).slice(0, 3).map((item, idx) => (
                        <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2">{item.project}</Typography>
                          <Chip label={`${item.hours}h`} size="small" variant="outlined" />
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Recent Activity */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardHeader title="Recent Tasks" titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }} 
                    action={<Button size="small" onClick={() => setActiveTab(1)}>View All</Button>} />
                  <CardContent sx={{ pt: 0 }}>
                    <Stack spacing={1}>
                      {(mockData?.tasks || []).slice(0, 3).map((task) => (
                        <Box key={task.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{task.title}</Typography>
                            <Typography variant="caption" color="text.secondary">{task.project}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip label={task.status} size="small" color={getStatusColor(task.status) as any} />
                            <Chip label={task.priority} size="small" variant="outlined" color={getPriorityColor(task.priority) as any} />
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 1: Tasks & Projects */}
          <TabPanel value={activeTab} index={1}>
            <Grid container spacing={3}>
              {/* Active Tasks */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardHeader title="Active Tasks" titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
                    action={<Button variant="contained" size="small" startIcon={<Plus size={14} />} onClick={() => setAssignTaskOpen(true)}>Assign Task</Button>} />
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Task</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Project</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Due Date</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(mockData?.tasks || []).map((task) => (
                          <TableRow key={task.id} hover>
                            <TableCell>{task.title}</TableCell>
                            <TableCell>{task.project}</TableCell>
                            <TableCell><Chip label={task.status} size="small" color={getStatusColor(task.status) as any} /></TableCell>
                            <TableCell><Chip label={task.priority} size="small" variant="outlined" color={getPriorityColor(task.priority) as any} /></TableCell>
                            <TableCell>{dayjs(task.dueDate).format('MMM D, YYYY')}</TableCell>
                            <TableCell>
                              <Button size="small" onClick={() => navigate(`/tasks`)}>View</Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </Grid>

              {/* Projects */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardHeader title="Assigned Projects" titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }} />
                  <CardContent sx={{ pt: 0 }}>
                    <Grid container spacing={2}>
                      {(mockData?.projects || []).map((project) => (
                        <Grid item xs={12} sm={6} md={4} key={project.id}>
                          <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                              <Typography variant="subtitle2" fontWeight={600}>{project.name}</Typography>
                              <Typography variant="caption" color="text.secondary">{project.role}</Typography>
                              <Box sx={{ mt: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                  <Typography variant="caption">Progress</Typography>
                                  <Typography variant="caption" fontWeight={600}>{project.progress}%</Typography>
                                </Box>
                                <LinearProgress variant="determinate" value={project.progress} sx={{ height: 6, borderRadius: 3 }} />
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
                                <Chip label={project.status} size="small" color={getStatusColor(project.status) as any} />
                                <Typography variant="caption">{project.tasks} tasks</Typography>
                              </Box>
                              <Button fullWidth size="small" sx={{ mt: 1.5 }} onClick={() => navigate(`/projects/${project.id}`)}>
                                View Project
                              </Button>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 2: KRA / KPI */}
          <TabPanel value={activeTab} index={2}>
            <Grid container spacing={3}>
              {/* KRAs */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight={600}>Key Result Areas (KRAs)</Typography>
                  <Button variant="contained" size="small" startIcon={<Plus size={14} />} onClick={() => navigate('/team/kra-kpi')}>
                    Manage KRAs
                  </Button>
                </Box>
                <Stack spacing={2}>
                  {(mockData?.kras || []).map((kra) => (
                    <Card key={kra.id} variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" fontWeight={600}>{kra.title}</Typography>
                            <Typography variant="body2" color="text.secondary">{kra.description}</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Chip label={kra.status.replace('_', ' ')} size="small" color={getStatusColor(kra.status) as any} />
                            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>Weight: {kra.weight}%</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ mb: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption">Progress</Typography>
                            <Typography variant="caption" fontWeight={600}>{kra.progress}%</Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={kra.progress}
                            sx={{ height: 8, borderRadius: 4 }}
                            color={kra.progress >= 75 ? 'success' : kra.progress >= 50 ? 'primary' : 'warning'}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">
                            {dayjs(kra.startDate).format('MMM D')} - {dayjs(kra.endDate).format('MMM D, YYYY')}
                          </Typography>
                          <Typography variant="caption" color={dayjs(kra.endDate).isBefore(dayjs()) ? 'error.main' : 'text.secondary'}>
                            {dayjs(kra.endDate).fromNow()}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </Grid>

              {/* KPIs */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Key Performance Indicators (KPIs)</Typography>
                <Grid container spacing={2}>
                  {(mockData?.kpis || []).map((kpi) => (
                    <Grid item xs={12} sm={6} md={3} key={kpi.id}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>{kpi.title}</Typography>
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="h4" fontWeight={700} color="primary">{kpi.current}</Typography>
                            <Typography variant="caption" color="text.secondary">/ {kpi.target} {kpi.unit}</Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min((kpi.current / kpi.target) * 100, 100)}
                            sx={{ height: 6, borderRadius: 3 }}
                            color={kpi.current >= kpi.target ? 'success' : kpi.current >= kpi.target * 0.7 ? 'primary' : 'warning'}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {kpi.frequency}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 3: Bandwidth */}
          <TabPanel value={activeTab} index={3}>
            <Grid container spacing={3}>
              {/* Capacity Overview */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardHeader title="Capacity Overview" titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }} />
                  <CardContent>
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                      <Typography variant="h2" fontWeight={700} color={member.utilizationPercent > 100 ? 'error' : member.utilizationPercent > 85 ? 'warning.main' : 'success.main'}>
                        {member.utilizationPercent}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Current Utilization</Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#e8f5e9', borderRadius: 2 }}>
                          <Typography variant="h5" fontWeight={700} color="#388e3c">{mockData?.bandwidth.availableHours}h</Typography>
                          <Typography variant="caption" color="#388e3c">Available</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#fff3e0', borderRadius: 2 }}>
                          <Typography variant="h5" fontWeight={700} color="#f57c00">{mockData?.bandwidth.allocatedHours}h</Typography>
                          <Typography variant="caption" color="#f57c00">Allocated</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Allocation Breakdown */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardHeader title="Allocation Breakdown" titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }} />
                  <CardContent>
                    <Stack spacing={2}>
                      {(mockData?.bandwidth.breakdown || []).map((item, idx) => (
                        <Box key={idx}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2">{item.project}</Typography>
                            <Typography variant="body2" fontWeight={600}>{item.hours}h ({item.percent}%)</Typography>
                          </Box>
                          <LinearProgress variant="determinate" value={item.percent} sx={{ height: 8, borderRadius: 4 }} />
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Weekly Availability */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardHeader title="Weekly Availability" titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }} />
                  <CardContent>
                    <Grid container spacing={1}>
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map((day, idx) => {
                        const allocated = Math.floor(Math.random() * 6) + 4;
                        const available = 8 - allocated;
                        return (
                          <Grid item xs key={day}>
                            <Box sx={{ textAlign: 'center', p: 1.5, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                              <Typography variant="subtitle2" fontWeight={600}>{day}</Typography>
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" display="block" color="success.main">{available}h free</Typography>
                                <Typography variant="caption" display="block" color="text.secondary">{allocated}h allocated</Typography>
                              </Box>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 4: Reviews */}
          <TabPanel value={activeTab} index={4}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight={600}>Performance Reviews</Typography>
              <Button variant="contained" startIcon={<Plus size={16} />} onClick={() => setAddReviewOpen(true)}>
                Add Review
              </Button>
            </Box>

            <Stack spacing={2}>
              {(mockData?.reviews || []).map((review) => (
                <Card key={review.id} variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>{review.period} Review</Typography>
                        <Typography variant="caption" color="text.secondary">
                          By {review.reviewer} • {dayjs(review.date).format('MMMM D, YYYY')}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Rating value={review.rating} precision={0.5} readOnly size="small" />
                        <Typography variant="body2" fontWeight={600}>({review.rating})</Typography>
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 2 }}>{review.feedback}</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" fontWeight={600} color="success.main">Strengths</Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                          {review.strengths.map((s) => (
                            <Chip key={s} label={s} size="small" color="success" variant="outlined" />
                          ))}
                        </Stack>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" fontWeight={600} color="warning.main">Areas for Improvement</Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                          {review.improvements.map((s) => (
                            <Chip key={s} label={s} size="small" color="warning" variant="outlined" />
                          ))}
                        </Stack>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </TabPanel>

          {/* Tab 5: Calendar */}
          <TabPanel value={activeTab} index={5}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardHeader title="Upcoming Schedule" titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
                    action={<Button size="small" onClick={() => navigate('/calendar')}>Open Calendar</Button>} />
                  <CardContent sx={{ pt: 0 }}>
                    <Stack spacing={1}>
                      {(mockData?.calendar || []).map((event) => (
                        <Box key={event.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, bgcolor: event.type === 'deadline' ? '#ffebee' : '#e3f2fd', borderRadius: 1 }}>
                          <Box sx={{ width: 50, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">{dayjs(event.date).format('MMM')}</Typography>
                            <Typography variant="h5" fontWeight={700}>{dayjs(event.date).format('D')}</Typography>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" fontWeight={600}>{event.title}</Typography>
                            <Typography variant="caption" color="text.secondary">{event.time}</Typography>
                          </Box>
                          <Chip label={event.type} size="small" color={event.type === 'deadline' ? 'error' : 'primary'} variant="outlined" />
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 6: Time Tracking */}
          <TabPanel value={activeTab} index={6}>
            <Grid container spacing={3}>
              {/* Time Summary */}
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>This Week</Typography>
                    <Typography variant="h3" fontWeight={700} color="primary">{mockData?.timeTracking.thisWeek.logged}h</Typography>
                    <Typography variant="caption" color="text.secondary">/ {mockData?.timeTracking.thisWeek.expected}h expected</Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(mockData?.timeTracking.thisWeek.logged || 0) / (mockData?.timeTracking.thisWeek.expected || 1) * 100}
                      sx={{ height: 8, borderRadius: 4, mt: 1 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Last Week</Typography>
                    <Typography variant="h3" fontWeight={700} color="success.main">{mockData?.timeTracking.lastWeek.logged}h</Typography>
                    <Typography variant="caption" color="text.secondary">/ {mockData?.timeTracking.lastWeek.expected}h expected</Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(mockData?.timeTracking.lastWeek.logged || 0) / (mockData?.timeTracking.lastWeek.expected || 1) * 100}
                      color="success"
                      sx={{ height: 8, borderRadius: 4, mt: 1 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>This Month</Typography>
                    <Typography variant="h3" fontWeight={700} color="info.main">{mockData?.timeTracking.thisMonth.logged}h</Typography>
                    <Typography variant="caption" color="text.secondary">/ {mockData?.timeTracking.thisMonth.expected}h expected</Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(mockData?.timeTracking.thisMonth.logged || 0) / (mockData?.timeTracking.thisMonth.expected || 1) * 100}
                      color="info"
                      sx={{ height: 8, borderRadius: 4, mt: 1 }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Time Breakdown */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardHeader title="Time Breakdown (This Week)" titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }} />
                  <CardContent>
                    <Stack spacing={2}>
                      {(mockData?.timeTracking.breakdown || []).map((item, idx) => (
                        <Box key={idx}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2">{item.task}</Typography>
                            <Typography variant="body2" fontWeight={600}>{item.hours}h ({item.percent}%)</Typography>
                          </Box>
                          <LinearProgress variant="determinate" value={item.percent} sx={{ height: 8, borderRadius: 4 }} />
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 7: Leave Approval */}
          <TabPanel value={activeTab} index={7}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>Leave Approval for {member.name}</Typography>
              <Typography variant="body2" color="text.secondary">Review and approve leave requests from this team member</Typography>
            </Box>

            <Grid container spacing={3}>
              {/* Leave Balance Overview */}
              <Grid item xs={12} md={3}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Available Balance</Typography>
                    <Typography variant="h4" fontWeight={700} color="primary" sx={{ mt: 1 }}>
                      {mockData?.leaveBalance?.available || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">days remaining</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Used This Year</Typography>
                    <Typography variant="h4" fontWeight={700} color="success.main" sx={{ mt: 1 }}>
                      {mockData?.leaveBalance?.used || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">days taken</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card variant="outlined" sx={{ bgcolor: 'warning.50' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Pending Approval</Typography>
                    <Typography variant="h4" fontWeight={700} color="warning.main" sx={{ mt: 1 }}>
                      {mockData?.pendingLeaveRequests?.length || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">requests</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Total Allocation</Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ mt: 1 }}>
                      {mockData?.leaveBalance?.total || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">days per year</Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Pending Requests - Priority Section */}
              {mockData?.pendingLeaveRequests && mockData.pendingLeaveRequests.length > 0 && (
                <Grid item xs={12}>
                  <Alert severity="warning" icon={<AlertCircle size={20} />} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {mockData.pendingLeaveRequests.length} Pending Request{mockData.pendingLeaveRequests.length > 1 ? 's' : ''} Require Your Action
                    </Typography>
                  </Alert>

                  <Card variant="outlined" sx={{ borderColor: 'warning.main', borderWidth: 2 }}>
                    <CardHeader 
                      title="Pending Leave Requests" 
                      titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
                      sx={{ bgcolor: 'warning.50' }}
                    />
                    <CardContent>
                      <Stack spacing={3}>
                        {mockData.pendingLeaveRequests.map((request: any) => {
                          const startDate = dayjs(request.startDate);
                          const endDate = dayjs(request.endDate);
                          const appliedDate = dayjs(request.createdAt);
                          const totalDays = request.totalDays || endDate.diff(startDate, 'day') + 1;
                          
                          return (
                            <Card key={request.id} variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                              <CardContent>
                                <Grid container spacing={2} alignItems="center">
                                  <Grid item xs={12} md={8}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                      <Chip 
                                        label={request.type || 'Leave'} 
                                        color={request.type === 'sick' ? 'error' : 'primary'} 
                                        size="small" 
                                      />
                                      <Chip label={`${totalDays} Day${totalDays > 1 ? 's' : ''}`} size="small" variant="outlined" />
                                      <Chip label="Pending" color="warning" size="small" />
                                    </Box>
                                    <Stack spacing={1}>
                                      <Box sx={{ display: 'flex', gap: 3 }}>
                                        <Box>
                                          <Typography variant="caption" color="text.secondary">From</Typography>
                                          <Typography variant="body2" fontWeight={600}>{startDate.format('MMM DD, YYYY')}</Typography>
                                        </Box>
                                        <Box>
                                          <Typography variant="caption" color="text.secondary">To</Typography>
                                          <Typography variant="body2" fontWeight={600}>{endDate.format('MMM DD, YYYY')}</Typography>
                                        </Box>
                                        <Box>
                                          <Typography variant="caption" color="text.secondary">Applied On</Typography>
                                          <Typography variant="body2" fontWeight={600}>{appliedDate.format('MMM DD, YYYY')}</Typography>
                                        </Box>
                                      </Box>
                                      {request.reason && (
                                        <Box>
                                          <Typography variant="caption" color="text.secondary" display="block">Reason:</Typography>
                                          <Typography variant="body2">{request.reason}</Typography>
                                        </Box>
                                      )}
                                    </Stack>
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <Stack spacing={1.5}>
                                      <Button 
                                        variant="contained" 
                                        color="success" 
                                        fullWidth
                                        startIcon={<Check size={18} />}
                                        onClick={() => alert('Leave approved!')}
                                      >
                                        Approve
                                      </Button>
                                      <Button 
                                        variant="outlined" 
                                        color="error" 
                                        fullWidth
                                        startIcon={<X size={18} />}
                                        onClick={() => alert('Leave rejected!')}
                                      >
                                        Reject
                                      </Button>
                                    </Stack>
                                  </Grid>
                                </Grid>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Leave History */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardHeader 
                    title="Leave History" 
                    titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
                  />
                  <CardContent>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Leave Type</TableCell>
                            <TableCell>Period</TableCell>
                            <TableCell>Days</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Reason</TableCell>
                            <TableCell>Approved By</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell><Chip label="Annual Leave" size="small" color="success" variant="outlined" /></TableCell>
                            <TableCell>
                              <Typography variant="body2" noWrap>
                                {dayjs().subtract(10, 'day').format('MMM DD')} - {dayjs().subtract(8, 'day').format('MMM DD, YYYY')}
                              </Typography>
                            </TableCell>
                            <TableCell><Typography fontWeight={600}>3</Typography></TableCell>
                            <TableCell><Chip label="Approved" size="small" color="success" /></TableCell>
                            <TableCell>Vacation trip</TableCell>
                            <TableCell>Sarah Chen</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell><Chip label="Casual Leave" size="small" color="primary" variant="outlined" /></TableCell>
                            <TableCell>
                              <Typography variant="body2" noWrap>
                                {dayjs().subtract(30, 'day').format('MMM DD')} - {dayjs().subtract(29, 'day').format('MMM DD, YYYY')}
                              </Typography>
                            </TableCell>
                            <TableCell><Typography fontWeight={600}>2</Typography></TableCell>
                            <TableCell><Chip label="Approved" size="small" color="success" /></TableCell>
                            <TableCell>Family function</TableCell>
                            <TableCell>Alex Johnson</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell><Chip label="Work From Home" size="small" color="info" variant="outlined" /></TableCell>
                            <TableCell>
                              <Typography variant="body2" noWrap>
                                {dayjs().subtract(45, 'day').format('MMM DD, YYYY')}
                              </Typography>
                            </TableCell>
                            <TableCell><Typography fontWeight={600}>1</Typography></TableCell>
                            <TableCell><Chip label="Rejected" size="small" color="error" /></TableCell>
                            <TableCell>Internet issues at home</TableCell>
                            <TableCell>Sarah Chen</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Leave Policy Reference */}
              <Grid item xs={12}>
                <Alert severity="info" icon={<FileText size={20} />}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>Company Leave Policy</Typography>
                  <Typography variant="body2">
                    <strong>Annual Allocations:</strong> Casual Leave: 12 days | Sick Leave: 10 days | Annual Leave: 15 days
                    <br />
                    <strong>Guidelines:</strong> Advance notice of 3 days required | Manager approval mandatory for leaves exceeding 3 days
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </TabPanel>
        </Box>
      </Card>

      {/* Add Review Dialog */}
      <Dialog open={addReviewOpen} onClose={() => setAddReviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Performance Review</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Rating</Typography>
              <Rating value={reviewRating} onChange={(_, value) => setReviewRating(value)} size="large" precision={0.5} />
            </Box>
            <TextField
              label="Feedback"
              multiline
              rows={4}
              fullWidth
              value={reviewFeedback}
              onChange={(e) => setReviewFeedback(e.target.value)}
              placeholder="Provide detailed feedback about performance..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddReviewOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveReview}>Save Review</Button>
        </DialogActions>
      </Dialog>

      {/* Assign Task Dialog */}
      <Dialog open={assignTaskOpen} onClose={() => setAssignTaskOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Task to {member.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Task Title" fullWidth placeholder="Enter task title" />
            <TextField label="Description" multiline rows={3} fullWidth placeholder="Enter task description" />
            <TextField label="Due Date" type="date" fullWidth InputLabelProps={{ shrink: true }} />
            <TextField label="Priority" select fullWidth SelectProps={{ native: true }}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignTaskOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => { setAssignTaskOpen(false); navigate('/tasks'); }}>Assign Task</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeamMemberDetailPage;
